import {
    saved as saved_common,
    session as session_common,
    tab as tab_common,
    temporary as temporary_common,
} from '@bitstillery/common/lib/state'
import {copy_object, merge_deep, pending} from '@bitstillery/common/lib/utils'
import {AlcoholFormatting, CartValidation, PricePreference, VolumeFormatting} from '@bitstillery/common/api/fact2server'
import type {Currency} from '@bitstillery/common/api/fact2server'
import {mark_ssr_hydration_env_deferral} from '@bitstillery/common/lib/env'
import {deserializeAllStates, registerState, type Store} from '@bitstillery/mithril'

import type {DeepPartial, State} from '@bitstillery/mithril'
import type {StateCommon} from '@bitstillery/common/lib/state'
import type {
    CartValidationError as BaseCartValidationError,
    CountriesOfOriginResponse,
    GetAccountResponse,
    GetActiveVouchersResponse,
    GetOfferResponse,
    GetPortalProfileResponse,
    PortalMenuCategory,
    SalesOrderCreditItemResponse,
    SalesOrderItemResponse,
    SalesOrderTBOItemResponse,
} from '@bitstillery/common/api/fact2server'
import type {SalesOrderAdditional} from '@bitstillery/common/models/sales_order'

import type {CartItemState, CartOfferItem} from '@/models/cart'
import type {ProductPhoto} from '@/models/offer'

// Override CartValidationError to have proper numeric types for valid_value and invalid_value
export interface CartValidationError extends Omit<BaseCartValidationError, 'valid_value' | 'invalid_value'> {
    valid_value: number
    invalid_value: number
}

/** The profile as state holds it: before it loads there is no artkey, currency or sales manager yet. */
export type PortalUser = Omit<GetPortalProfileResponse, 'artkey' | 'currency' | 'sales_manager'> & {
    artkey: number | null
    currency: Currency | ''
    sales_manager: Omit<GetPortalProfileResponse['sales_manager'], 'artkey'> & {artkey: number | null}
}

export interface StatePortal extends StateCommon {
    cart: {
        // This is a shared model for ProductQuantity amounts
        artkey: null | number
        errors: {
            [CartValidation.MIN_QUANTITY]: Record<string, CartValidationError>
            [CartValidation.PRICE_WAS_RAISED]: Record<string, CartValidationError>
            [CartValidation.QUANTITY]: Record<string, CartValidationError>
            [CartValidation.STEP_SIZE]: Record<string, CartValidationError>
        }
        finalize: {
            selection: boolean
        }
        includes_excise: boolean
        // Keyed by case artkey.
        items: Record<number, CartItemState>
        item_changed: null | string | number
        loading: boolean
        // The offer item behind each cart line, keyed by case artkey.
        offer_items: Record<number, CartOfferItem>
        reference: null | string
        sales_order_additionals: SalesOrderAdditional[]
        sales_order_credit_items: SalesOrderCreditItemResponse[]
        sales_order_items: SalesOrderItemResponse[]
        sales_order_tbo_items: SalesOrderTBOItemResponse[]
        show_bottles: boolean
        unavailable: []
        vouchers: {
            active: boolean
            available: GetActiveVouchersResponse[]
        }
    }
    categories: {
        // Category name by artkey.
        lookup: Record<string, string>
        // Persisted: merge_deep lets whatever localStorage holds replace the default, null included.
        menu: PortalMenuCategory[] | null
    }
    countries_of_origin: Record<string, CountriesOfOriginResponse[]>
    identity: {
        artkey: null | number
        demo?: boolean
        exp: null | number
        token: null | string
        user: State<PortalUser>
    }
    offers: {
        export: {
            /** Used to toggle selection checkboxes with in the offer items list */
            active: boolean
            alcohol: {
                /** Persisted selection of alcohol formatting */
                selection: AlcoholFormatting
            }
            size: {
                /** Persisted selection of volume formatting */
                selection: VolumeFormatting
            }
        }
        personal: GetOfferResponse[]
        hash: string
        title: string
        records: []
        special: number
        new_arrivals: number
        favourites: number
        updated_favourites: number
    }
    order: {
        empty: never[]
        pending: never[]
        sort: {
            by: 'created_on'
            order: 'desc'
        }
    }
    page: {
        compiled_js: null | string
        header: {
            collapsed: boolean
        }
        hero_background: string
        hero_mode: string
        path: null | string
        error: null | string
    }
    popup: {
        /** Dismissal of the `cms-popup` fragment popup, which has no row of its own to key on. */
        fragment: null | string
        offer: null | string
        promotion: null | string
    }
    // Keyed by offer item artkey; an empty list marks an artkey that was asked for and has no photos.
    product_photos: Record<number, ProductPhoto[]>
    promotion: {
        active: boolean
        active_grace: boolean
        /** Identifier for the promotion API */
        artkey: number
        current: string
        end_date: null | string
        grace_date: string
        gifts: {
            description: string
            id: string
            link: string
            title: string
            slug: string
            tokens_required: number
            url: string
        }[]
        icon: string
        /** Simple local persistance for gift slug and selected amount */
        loading: boolean
        lookup: Record<string, number>
        name: string
        selection: null | string
        slogan: string
        slug: string
        start_date: null | string
        terms: {
            collapsed: boolean
        }
        type: null | string
        /** Spent amount of money on sales orders during the promotion */
        turnover_draft: number
        turnover_redeemable: number
        token_icon: string
        /** Amount of money that represents one token */
        token_value: number
        tokens_draft: number
        tokens_redeemable: number
        /** Selected amount of tokens for redeeming */
        tokens_selected: number
        /** Determines whether a promotion can be redeemed */
        unlocked: boolean
    }
    portal: {
        account: GetAccountResponse
    }
    suggestions: {
        selected: {
            index: null | number
        }
        types: {
            brand_holder: {
                name: string
                options: never[]
            }
            category: {
                name: string
                options: never[]
            }
            search: {
                name: string
                options: never[]
            }
            tbos: {
                name: string
                options: []
            }
        }
    }
    /** Shop UI persisted with `saved` (localStorage). */
    shop: {
        collection_view_mode: 'grid' | 'list'
    }
}

export const temporary = merge_deep(
    {
        cart: {
            artkey: null,
            errors: {
                [CartValidation.QUANTITY]: {},
                [CartValidation.MIN_QUANTITY]: {},
                [CartValidation.PRICE_WAS_RAISED]: {},
                [CartValidation.STEP_SIZE]: {},
            },
            finalize: {
                selection: true,
            },
            item_changed: null,
            items: {},
            loading: true,
            offer_items: {},
            reference: null,
            sales_order_additionals: [],
            sales_order_credit_items: [],
            sales_order_items: [],
            sales_order_tbo_items: [],
            unavailable: [],
            vouchers: {
                active: false,
                available: [],
            },
        },
        countries_of_origin: {},
        currencies: {
            default: 'EUR',
        },
        identity: {
            artkey: null,
            exp: null,
            user: {
                artkey: null,
                country_code: '',
                currency: '',
                customs_visibility: '',
                email_address: '',
                first_name: '',
                has_api_access: false,
                include_excise_in_price: true,
                language: '',
                last_name: '',
                minimum_order_amount: '',
                mobile_phone_number: '',
                name: '',
                phone_number: '',
                price_preference: PricePreference.Case,
                relation_name: '',
                timezone: 'Europe/Amsterdam',
                sales_manager: {
                    artkey: null,
                    email_address: '',
                    name: '',
                    phone_number: '',
                },
                show_excise: false,
                tawk_hash: '',
                username: '',
            },
        },
        offers: {
            export: {
                active: false,
            },
            personal: [],
            records: [],
            special: 0,
        },
        order: {
            empty: [],
            pending: [],
            sort: {
                by: 'created_on',
                order: 'desc',
            },
        },
        product_photos: {},
        promotion: {
            active: false,
            active_grace: false,
            end_date: null,
            gifts: [],
            icon: 'bean',
            loading: true,
            name: '',
            selection: null,
            slogan: '',
            start_date: null,
            terms: {
                collapsed: true,
            },
            token_icon: '',
            token_value: 0,
            tokens_draft: 0,
            tokens_selected: 0,
            turnover_draft: 0,
            turnover_redeemable: 0,
            type: null,
        },
        page: {
            compiled_js: null,
            header: {
                collapsed: false,
            },
            hero_background: '',
            hero_mode: 'full',
            path: null,
            error: null,
        },
        suggestions: {
            selected: {
                index: null,
            },
            // These types map directly to filters
            types: {
                brand_holder: {
                    name: 'filters.title.brand_holder',
                    options: [],
                },
                category: {
                    // @i18n ignore: filters.types.category
                    name: 'filters.types.category',
                    options: [],
                },
                search: {
                    // @i18n ignore: filters.types.stock
                    name: 'filters.types.stock',
                    options: [],
                },
                tbos: {
                    // @i18n ignore: filters.types.tbos
                    name: 'filters.types.tbos',
                    options: [],
                },
            },
        },
        /** `shop` lives only on `saved` — do not put it here; `temporary` is merged after localStorage and would reset `collection_view_mode` every load. */
    } satisfies DeepPartial<StatePortal>,
    copy_object(temporary_common),
)

export const tab = merge_deep({}, copy_object(tab_common))

export const session = merge_deep({}, copy_object(session_common))

export const saved = merge_deep(
    {
        cart: {
            show_bottles: false,
        },
        categories: {
            lookup: {},
            menu: [],
        },
        identity: {
            token: null,
        },
        portal: {
            account: pending<GetAccountResponse>(),
        },
    } satisfies DeepPartial<StatePortal>,
    copy_object(saved_common),
)

/**
 * Cookie-tier state: persisted in a single JSON cookie instead of localStorage. A cookie rides along
 * on the SSR document request, so the server can render these preferences and the client hydrates to
 * the same value — no flash, and no clobber (which `saved`/localStorage suffers, since it is never
 * sent to the server). Keep this small and render-affecting; cookies ship on every request (the
 * Store enforces MAX_COOKIE_BYTES).
 *
 * These are the genuine, persisted client preferences:
 * - language: chosen portal language (rendered during SSR so anonymous users keep it without a flash;
 *   logged-in users' profile language still wins via `init_language`'s precedence order)
 * - popup: offer/promotion/fragment dismissal timestamps (rendered during SSR — the original flash)
 * - settings.beta: beta toggle (footer ×7 / PanelLeft)
 * - shop.collection_view_mode: grid/list
 * - offers.export: alcohol/size formatting selection
 * - promotion.lookup: selected gift slug + amount
 *
 * `panels`/`issues`/`cart.show_bottles` are intentionally NOT here: panel collapse shouldn't persist,
 * `issues` is discover-only, and `cart.show_bottles` is API-sourced — they stay in `saved`.
 */
export const cookie = merge_deep({
    // Empty until a visitor picks a language; overwritten by the request cookie (SSR) / document.cookie
    // (client). Kept in sync with `saved_common.language` so first-time visitors render the same default
    // server & client — and left unset so `init_language` can fall back to the domain's language instead
    // of mistaking a hardcoded default for a preference.
    language: '',
    // Light/dark preference ('light' | 'dark' | 'auto'). In the cookie tier so SSR can render the right
    // `data-theme` on <html> (see server.ts) without a flash; the client hydrates to the same value.
    theme: 'light',
    popup: {
        fragment: null,
        offer: null,
        promotion: null,
    },
    settings: {
        beta: false,
    },
    shop: {
        collection_view_mode: 'grid',
    },
    offers: {
        export: {
            alcohol: {
                selection: AlcoholFormatting.Percentage,
            },
            size: {
                selection: VolumeFormatting.Cl,
            },
        },
    },
    promotion: {
        lookup: {},
    },
} satisfies DeepPartial<StatePortal>)

// Cookie keys must have a single home: strip them from `saved` so they aren't also written to
// localStorage. `settings` and `language` in particular arrive via `saved_common`; the rest are
// already absent.
for (const key of Object.keys(cookie)) {
    delete (saved as Record<string, unknown>)[key]
}

export type PrepareAppStateSource = 'ssr' | 'hydration' | 'spa'

export interface PrepareAppStateOptions {
    source: PrepareAppStateSource
    store: Store<StateCommon>
    sessionData?: Record<string, unknown> | undefined
    serializedState?: Record<string, unknown> | undefined
    /** Parsed cookie-tier preferences from the SSR request (server only); seeds the `cookie` template. */
    cookieData?: Record<string, unknown> | undefined
}

const STATE_NAME = 'portalStore'

/**
 * Prepares the store for use: loads templates, optionally merges session data
 * (SSR), deserializes hydrated state (client after SSR), or just loads defaults (SPA).
 */
export function prepare_state(options: PrepareAppStateOptions): void {
    const {source, store, sessionData, serializedState, cookieData} = options

    if (source === 'ssr') {
        const sessionTemplate = {...session, ...sessionData}
        // Seed the cookie tier from the request cookie (parsed by the SSR server). There is no
        // document on the server, so the values are injected here instead of read in store.load().
        const cookieTemplate = cookieData ? merge_deep(copy_object(cookie), copy_object(cookieData)) : cookie
        const tempMergedInitial = {
            ...copy_object(saved),
            ...copy_object(temporary),
            ...copy_object(tab),
            ...copy_object(sessionTemplate),
            ...copy_object(cookieTemplate),
        }
        registerState(STATE_NAME, store.state, tempMergedInitial)
        store.load(saved, temporary, tab, sessionTemplate, cookieTemplate)
        return
    }

    if (source === 'hydration') {
        mark_ssr_hydration_env_deferral()
        // Must register before load + deserialize (same as SSR). Otherwise `portalStore` is missing
        // from the client registry and `deserializeAllStates` skips SSR payload — stale `cms_page`
        // from localStorage wins and the client shows an old compiled view after a fresh SSR HTML.
        const tempMergedInitial = {
            ...copy_object(saved),
            ...copy_object(temporary),
            ...copy_object(tab),
            ...copy_object(session),
            ...copy_object(cookie),
        }
        registerState(STATE_NAME, store.state, tempMergedInitial)
        // Client preferences live in the cookie tier, which store.load() reads from document.cookie.
        // The SSR snapshot was seeded from the same request cookie, so the deserialized state below
        // already agrees with what the client loaded — no post-deserialize overlay needed.
        store.load(saved, temporary, tab, session, cookie)
        if (serializedState) {
            const {__meta: _ssr_meta, ...state_for_deserialize} = serializedState as Record<string, unknown> & {
                __meta?: unknown
            }
            deserializeAllStates(state_for_deserialize)
        }

        store.state.scheme.version = saved.scheme.version
        return
    }

    // Spa
    store.load(saved, temporary, tab, session, cookie)
}
